import * as graphql from 'generated/graphql';
import EvaluationAnswerComposer from './EvaluationAnswerComposer';
import EvaluationAnswerCard from './EvaluationAnswerCard';
import useDetectPrint from '../hooks/useDetectPrint';
import PrivateAnswerDisplay from './display/PrivateAnswerDisplay';
import UnsharedAnswerDisplay from './display/UnsharedAnswerDisplay';
import EmptyAnswerDisplay from './display/EmptyAnswerDisplay';
import EvaluationAnswerDisplay from './EvaluationAnswerDisplay';
import IrrelevantAnswerDisplay from './display/IrrelevantAnswerDisplay';
import EvaluationAnswerLine from './EvaluationAnswerLine';

const isEmptyEvaluationSubmissionItem = (item: graphql.EvaluationSubmissionItem) => {
	if (item.type === graphql.EvaluationSectionItemType.OpenQuestion) {
		return (
			!item.openQuestion ||
			item.openQuestion.ops
				.map((op: { insert?: string }) => op.insert ?? '')
				.join('')
				.trim().length === 0
		);
	}

	if (item.type === graphql.EvaluationSectionItemType.Sentiment) {
		return item.sentiment === null || item.sentiment === undefined;
	}

	if (item.type === graphql.EvaluationSectionItemType.Stars) {
		return item.stars === null || item.stars === undefined;
	}

	if (item.type === graphql.EvaluationSectionItemType.Competency) {
		return item.competency === null || item.competency === undefined;
	}

	if (item.type === graphql.EvaluationSectionItemType.ReviewGoals) {
		return item.reviewGoals === null || item.reviewGoals === undefined;
	}

	return false;
};

export default function EvaluationSubmissionAnswer(props: {
	sectionIndex: number;
	itemIndex: number;
	goal?: graphql.EvaluationSubmissionGoal;
	submission: graphql.EvaluationSubmission;
	party: graphql.EvaluationSubmissionParty;
	onSave: () => void;
	isSaving: boolean;
}) {
	const isPrinting = useDetectPrint();
	const sectionItem =
		props.submission.evaluationForm.sections[props.sectionIndex].items[props.itemIndex]!;

	const partyResult = props.submission[props.party.toLowerCase() as 'employee' | 'manager'];
	const partyResultItem = partyResult.sections[props.sectionIndex].items[props.itemIndex]!;
	const isEmployee = props.party === graphql.EvaluationSubmissionParty.Employee;

	if (
		(sectionItem.fillInBy === graphql.EvaluationFillInBy.Manager && isEmployee) ||
		(sectionItem.fillInBy === graphql.EvaluationFillInBy.Employee && !isEmployee)
	) {
		return (
			<EvaluationAnswerLine user={partyResult.user} side={isEmployee ? 'left' : 'right'} withAvatar>
				<IrrelevantAnswerDisplay result={partyResult} />
			</EvaluationAnswerLine>
		);
	}

	if (partyResult.viewerCanUpdate && !isPrinting) {
		return (
			<EvaluationAnswerCard user={partyResult.user} side={isEmployee ? 'left' : 'right'} withAvatar>
				<EvaluationAnswerComposer
					submission={props.submission}
					section={props.sectionIndex}
					item={props.itemIndex}
					goal={props.goal}
					result={partyResult}
					onSave={props.onSave}
					isSaving={props.isSaving}
					autosave={!partyResult.hasShared}
				/>
			</EvaluationAnswerCard>
		);
	}

	if (partyResult.hasShared && !partyResultItem.viewerCanView) {
		return (
			<EvaluationAnswerCard
				user={partyResult.user}
				side={isEmployee ? 'left' : 'right'}
				variant="empty"
				withAvatar
			>
				<PrivateAnswerDisplay result={partyResult} />
			</EvaluationAnswerCard>
		);
	}

	if (!partyResult.hasShared && !partyResultItem.viewerCanView) {
		return (
			<EvaluationAnswerCard
				user={partyResult.user}
				side={isEmployee ? 'left' : 'right'}
				variant="empty"
				withAvatar
			>
				<UnsharedAnswerDisplay result={partyResult} />
			</EvaluationAnswerCard>
		);
	}

	if (isEmptyEvaluationSubmissionItem(partyResultItem)) {
		return (
			<EvaluationAnswerCard
				user={partyResult.user}
				side={isEmployee ? 'left' : 'right'}
				withAvatar
				variant="empty"
			>
				<EmptyAnswerDisplay result={partyResult} />
			</EvaluationAnswerCard>
		);
	}

	return (
		<EvaluationAnswerCard user={partyResult.user} side={isEmployee ? 'left' : 'right'} withAvatar>
			<EvaluationAnswerDisplay
				resultItem={partyResultItem}
				sectionItem={sectionItem}
				party={props.party}
				submission={props.submission}
				sectionIndex={props.sectionIndex}
				itemIndex={props.itemIndex}
				goal={props.goal}
			/>
		</EvaluationAnswerCard>
	);
}
